import React, { useCallback } from "react";
import Typography from "@mui/material/Typography";
import IntegerInputField from "~styled/integerInputField/IntegerInputField";
import pxToRem from "~libs/pxToRem";
import { makeStyles } from "@mui/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import ItemLoadingCard from "./ItemLoadingCard";
import IconEdit from "~assets/icons/ic_edit.svg";
import useField from "~hooks/useField";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import type { FieldChangeEvent } from "./types";

interface ItemNumericCardProps {
  id?: string;
  label?: string;
  value?: string | null;
  onChange?: (event: FieldChangeEvent<string>) => void;
  placeholder?: string;
  required?: boolean;
  displayError?: boolean;
  focus?: boolean;
  onBlur?: () => void;
  readOnly?: boolean;
  isLoading?: boolean;
  helpText?: string;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2%",
    borderBottom: "1px solid #8B8B8B",
  },
  caption: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "left",
    color: "#8B8B8B",
  },
  body: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    maxWidth: pxToRem(270),
    width: "50%",
  },
  value: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.38,
    letterSpacing: "normal",
    textAlign: "right",
    color: "#424242",
  },
  input: {
    padding: "5%",
    textAlign: "end",
    width: "100%",
    "& .Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
    "&:Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
  },
  buttonLink: {
    display: "flex",
    alignItems: "center",
    color: "#f77221",
    paddingLeft: 8,
    cursor: "pointer",
  },
  icon: { color: "#6240a5" },
  helpIcon: {
    color: "#8B8B8B",
    transform: "scale(0.8)",
  },
}));

const errorText = "Requerido";

const ItemNumericCard: React.FC<ItemNumericCardProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  displayError,
  focus,
  onBlur,
  readOnly,
  isLoading,
  helpText,
}) => {
  const classes = useStyles();

  const validateFunction = useCallback(handleValidate(required), [required]);

  const [localValue, setValue, handleEdit, reference] = useField(
    value,
    validateFunction,
    focus,
    isLoading,
    readOnly
  );

  const handleBlur = () => {
    if (onBlur) onBlur();
  };

  const handleChangeValue = (value: string) => {
    setValue((prev) => ({ ...prev, value: value }));
    if (onChange)
      onChange({
        value: value,
        valid: validateFunction(value),
        focus: false,
      });
  };

  const showError = !localValue.valid && displayError;

  if (isLoading) return <ItemLoadingCard label={label} />;

  return (
    <div className={classes.container}>
      <Typography className={classes.caption}>{label}</Typography>
      <div className={classes.body}>
        {localValue.editing && (
          // @ts-ignore
          <IntegerInputField
            id={id}
            {...{ style: { width: "100%" } } as any}
            inputProps={{ className: classes.input }}
            onChange={(event) => handleChangeValue(event.target.value)}
            value={localValue.value}
            onBlur={handleBlur}
            variant="outlined"
            placeholder={placeholder}
            error={showError}
            inputRef={reference}
          />
        )}
        {helpText && (
          <Tooltip title={helpText}>
            <HelpOutlineIcon className={classes.helpIcon} />
          </Tooltip>
        )}
        {!localValue.editing && (
          <>
            {value && (
              <Typography className={classes.value}>{value}</Typography>
            )}
            {!value && <div>&mdash;</div>}
            {!readOnly && (
              <div
                className={classes.buttonLink}
                onClick={() => handleEdit(true)}
              >
                <img alt="" src={IconEdit} className={classes.icon} />
              </div>
            )}
          </>
        )}
        {!localValue.valid && (
          <Tooltip title={errorText}>
            <div className={classes.buttonLink}>
              <InfoOutlinedIcon style={{ width: 30, height: 30 }} />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default ItemNumericCard;

const handleValidate =
  (required: boolean | undefined) =>
  (value: string | null): boolean => {
    return Boolean(!required || value);
  };
